import {
  createEntityAdapter,
  EntityAdapter,
  EntityState,
  Update,
} from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { ProjectLocationPlanCoreModel } from '../../models/project-location-plan-core.model';
import { ProjectLocationPlanCoreActions } from './project-location-plan-core.actions';

export interface ProjectLocationPlanCoreTypeState
  extends EntityState<ProjectLocationPlanCoreModel.API.CoreTypeResponse> {
  isLoading: boolean;
  hasLoaded: boolean;
}

export interface ProjectLocationPlanCoreState
  extends EntityState<ProjectLocationPlanCoreModel.PlanCoreDocument> {
  isLoading: boolean;
  hasLoaded: boolean;
  selectedCore: ProjectLocationPlanCoreModel.API.CoreTypeResponse | null;
  selectedCoreCount: number;
  coreTypeState: ProjectLocationPlanCoreTypeState;
  isSaving: boolean;
  isCopying: boolean;
  isDeleting: boolean;
}

export const projectLocationPlanCoreAdapter: EntityAdapter<ProjectLocationPlanCoreModel.PlanCoreDocument> =
  createEntityAdapter<ProjectLocationPlanCoreModel.PlanCoreDocument>({
    selectId: (core) => core._id,
  });

export const projectLocationPlanCoreTypeAdapter: EntityAdapter<ProjectLocationPlanCoreModel.API.CoreTypeResponse> =
  createEntityAdapter<ProjectLocationPlanCoreModel.API.CoreTypeResponse>({
    selectId: (coreType) => coreType._id,
  });

const initialCoreTypeState: ProjectLocationPlanCoreTypeState =
  projectLocationPlanCoreTypeAdapter.getInitialState({
    isLoading: false,
    hasLoaded: false,
  });

const initialState: ProjectLocationPlanCoreState =
  projectLocationPlanCoreAdapter.getInitialState({
    isLoading: false,
    hasLoaded: false,
    selectedCore: null,
    selectedCoreCount: 1,
    isSaving: false,
    isCopying: false,
    isDeleting: false,
    coreTypeState: initialCoreTypeState,
  });

export const projectLocationPlanCoreStateReducer = createReducer(
  initialState,
  on(ProjectLocationPlanCoreActions.resetToInitialState, () => ({
    ...initialState,
  })),
  on(ProjectLocationPlanCoreActions.loadProjectLocationPlanCores, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(ProjectLocationPlanCoreActions.setSelectedCoreDevice, (state, { selectedCore, count }) => ({
    ...state,
    selectedCore,
    selectedCoreCount: selectedCore ? Math.max(1, count ?? 1) : 1,
  })),
  on(ProjectLocationPlanCoreActions.updatePlanCore, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(ProjectLocationPlanCoreActions.loadCoreTypes, (state) => ({
    ...state,
    coreTypeState: {
      ...state.coreTypeState,
      isLoading: true,
    },
  })),
  on(
    ProjectLocationPlanCoreActions.loadCoreTypesSuccess,
    (state, { coreTypes }) => {
      return {
        ...state,
        coreTypeState: projectLocationPlanCoreTypeAdapter.setAll(coreTypes, {
          ...state.coreTypeState,
          isLoading: false,
          hasLoaded: true,
        }),
      };
    },
  ),
  on(ProjectLocationPlanCoreActions.loadCoreTypesFail, (state) => ({
    ...state,
    coreTypeState: {
      ...state.coreTypeState,
      isLoading: false,
      hasLoaded: true,
    },
  })),
  on(
    ProjectLocationPlanCoreActions.loadProjectLocationPlanCoresSuccess,
    (state, { cores }) => {
      return projectLocationPlanCoreAdapter.setAll(cores, {
        ...state,
        isLoading: false,
        hasLoaded: true,
        activeRequestPlanId: null,
      });
    },
  ),
  on(ProjectLocationPlanCoreActions.updateProjectLocationPlanCoresSuccess,
    (state, { cores }) => {
      const updates: Update<ProjectLocationPlanCoreModel.PlanCoreDocument>[] = cores.map(
        (core) => ({
          id: core._id,
          changes: core,
        }),
      );

      return projectLocationPlanCoreAdapter.updateMany(updates, state);
    },
  ),
  on(ProjectLocationPlanCoreActions.loadProjectLocationPlanCoresFail, (state) => ({
    ...state,
    isLoading: false,
    hasLoaded: true,
  })),
  on(
    ProjectLocationPlanCoreActions.loadPlanCoresSuccess,
    (state, { cores }) => {
      return projectLocationPlanCoreAdapter.setAll(cores, {
        ...state,
      });
    },
  ),
  on(
    ProjectLocationPlanCoreActions.saveCore,
    (state) => {
      return {
        ...state,
        isSaving: true,
      };
    },
  ),
  on(
    ProjectLocationPlanCoreActions.saveCoreSuccess,
    (state, { core }) => {
      return projectLocationPlanCoreAdapter.addOne(core, { ...state, isSaving: false });
    },
  ),
  on(
    ProjectLocationPlanCoreActions.saveCoreFail,
    (state) => {
      return {
        ...state,
        isSaving: false,
      };
    },
  ),
  on(
    ProjectLocationPlanCoreActions.updatePlanCoreSuccess,
    (state, { core }) => {
      return projectLocationPlanCoreAdapter.updateOne(
        {
          id: core._id,
          changes: core,
        },
        {
          ...state,
          isSaving: false,
        },
      );
    },
  ),
  on(ProjectLocationPlanCoreActions.updatePlanCoreFail, (state) => ({
    ...state,
    isSaving: false,
  })),
  on(ProjectLocationPlanCoreActions.copyCore, (state) => ({
    ...state,
    isCopying: true,
  })),
  on(ProjectLocationPlanCoreActions.copyCoreSuccess, (state, { core }) => {
    return projectLocationPlanCoreAdapter.setOne(core, {
      ...state,
      isCopying: false,
    });
  }),
  on(ProjectLocationPlanCoreActions.copyCoreFail, (state) => ({
    ...state,
    isCopying: false,
  })),
  on(ProjectLocationPlanCoreActions.deleteCore, (state) => ({
    ...state,
    isDeleting: true,
  })),
  on(ProjectLocationPlanCoreActions.deleteCoreSuccess, (state, { core }) => {
    return projectLocationPlanCoreAdapter.removeOne(core._id, {
      ...state,
      isDeleting: false,
    });
  }),
  on(ProjectLocationPlanCoreActions.deleteCoreFail, (state) => ({
    ...state,
    isDeleting: false,
  })),
  on(ProjectLocationPlanCoreActions.bulkDeleteCores, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(ProjectLocationPlanCoreActions.bulkDeleteCoresSuccess,
    (state, { coreIds }) => {
      return projectLocationPlanCoreAdapter.removeMany(coreIds, {
        ...state,
        isSaving: false,
      });
    },),
  on(ProjectLocationPlanCoreActions.bulkDeleteCoresFail, (state) => ({
    ...state,
    isSaving: false,
  })),
  on(ProjectLocationPlanCoreActions.bulkCreateCores, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(ProjectLocationPlanCoreActions.bulkCreateCoresSuccess,
    (state, { cores }) => {
      return projectLocationPlanCoreAdapter.addMany(cores, {
        ...state,
        isSaving: false,
      });
    },),
  on(ProjectLocationPlanCoreActions.bulkCreateCoresFail, (state) => ({
    ...state,
    isSaving: false,
  })),
);

export const projectLocationPlanCoreFeature = createFeature({
  name: 'projectLocationPlanCore',
  reducer: projectLocationPlanCoreStateReducer,
  extraSelectors: ({ selectProjectLocationPlanCoreState }) => {
    const { selectAll, selectEntities } =
      projectLocationPlanCoreAdapter.getSelectors(
        selectProjectLocationPlanCoreState,
      );
    const {
      selectAll: selectAllCoreTypes,
      selectEntities: selectCoreTypeEntities,
    } = projectLocationPlanCoreTypeAdapter.getSelectors(
      createSelector(
        selectProjectLocationPlanCoreState,
        (state) => state.coreTypeState,
      ),
    );

    const selectCoreTypeState = createSelector(
      selectProjectLocationPlanCoreState,
      (state) => state.coreTypeState,
    );

    const selectSelectedCore = createSelector(
      selectProjectLocationPlanCoreState,
      (state): ProjectLocationPlanCoreModel.API.CoreTypeResponse | null => state.selectedCore
    );

    const selectSelectedCoreCount = createSelector(
      selectProjectLocationPlanCoreState,
      (state): number => state.selectedCoreCount,
    );

    const selectCoreTypesIsLoading = createSelector(
      selectCoreTypeState,
      (state): boolean => state.isLoading,
    );

    const selectCoreTypesHasLoaded = createSelector(
      selectCoreTypeState,
      (state): boolean => state.hasLoaded,
    );

    return {
      selectProjectLocationPlanCoreEntities: selectEntities,
      selectSelectedCore,
      selectSelectedCoreCount,
      selectCoreTypeEntities,
      selectCoreTypes: selectAllCoreTypes,
      selectCoreTypesIsLoading,
      selectCoreTypesHasLoaded,
      selectProjectLocationPlanCores: selectAll,
      selectProjectLocationPlanCoreById: (coreId: string) =>
        createSelector(selectEntities, (entities) => {
          return entities[coreId] ?? null;
        }),
      selectProjectLocationPlanCoresByPlanId: (planId: string) =>
        createSelector(selectAll, (cores) => {
          return cores.filter((core) => core.planId === planId);
        }),
    };
  },
});
